import { useState } from 'react';

interface LiteYouTubeProps {
  videoId: string;
  title: string;
  /** 'landscape' for 16:9 desktop, 'portrait' for 9:16 mobile shorts */
  aspect?: 'landscape' | 'portrait';
}

/**
 * Lightweight YouTube facade.
 *
 * On first render it shows only a static thumbnail + play button (no iframe,
 * no YouTube JS).  The real iframe — and all YouTube scripts — are only
 * injected into the DOM when the user clicks play.  This saves ~800 kB of
 * third-party JS on initial page load.
 */
export function LiteYouTube({ videoId, title, aspect = 'landscape' }: LiteYouTubeProps) {
  const [activated, setActivated] = useState(false);

  const thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;

  const aspectClass = aspect === 'portrait' ? 'aspect-[9/16]' : 'aspect-video';

  if (activated) {
    return (
      <div className={`${aspectClass} rounded-xl overflow-hidden bg-slate-900`}>
        <iframe
          width="100%"
          height="100%"
          src={embedUrl}
          title={title}
          style={{ border: 'none' }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setActivated(true)}
      aria-label={`Play: ${title}`}
      className={`${aspectClass} relative w-full rounded-xl overflow-hidden bg-slate-900 group cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-500`}
    >
      {/* Thumbnail */}
      <img
        src={thumbnailUrl}
        alt={title}
        width={aspect === 'portrait' ? 360 : 640}
        height={aspect === 'portrait' ? 640 : 360}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Dark overlay on hover */}
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-200" />

      {/* Play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-red-600 group-hover:bg-red-500 flex items-center justify-center shadow-2xl transition-all duration-200 group-hover:scale-110">
          {/* YouTube-style play triangle */}
          <svg
            viewBox="0 0 24 24"
            className="w-7 h-7 text-white fill-current translate-x-0.5"
            aria-hidden="true"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    </button>
  );
}
